import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { FormGroup } from '@angular/forms';
import { addRemoveError, getControl, getGroup } from '@shared/helpers/form.helper';
import { toNum } from '@shared/helpers/math.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { INCORRECT_VALUE, REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { getContribBiennaleControl } from './helpers/contrib-biennale-form.helpers';
import { formPaths } from '@shared/constants/form-paths.const';
import { isEqual } from 'lodash-es';

export const contribBiennaleDatiSorgenteRules: FormGroupRules = {
    my_SociInseriti: (form: FormGroup) => {
        const my_EffettivitaBaseSocialeGroup = getGroup(form, formPaths.baseSociale);
        const my_SociInseritiControl = getContribBiennaleControl(form, 'my_SociInseriti');
        const chiusuraEsercizio: string = getControl(my_EffettivitaBaseSocialeGroup, 'my_ChiusuraEsercizio')?.value;
        const anno1__1: string = getContribBiennaleControl(form, 'my_Anno1__1')?.value;
        const my_SociInseriti = toNum(my_SociInseritiControl?.value);

        const data1EsPrec: string = getControl(my_EffettivitaBaseSocialeGroup, 'my_Data1EsPrec')?.value;
        const data2EsPrec: string = getControl(my_EffettivitaBaseSocialeGroup, 'my_Data2EsPrec')?.value;
        const sociFisCoop = toNum(getControl(my_EffettivitaBaseSocialeGroup, 'my_SociFisCoopPrimo')?.value);
        const my_SociGiuCoop = toNum(getControl(my_EffettivitaBaseSocialeGroup, 'my_SociGiuCoopPrimo')?.value);
        const my_SociFisFin = toNum(getControl(my_EffettivitaBaseSocialeGroup, 'my_SociFisFinPrimo')?.value);
        const my_SociGiuFin = toNum(getControl(my_EffettivitaBaseSocialeGroup, 'my_SociGiuFinPrimo')?.value);
        const my_SociSpec = toNum(getControl(my_EffettivitaBaseSocialeGroup, 'my_SociSpecPrimo')?.value);
        const totSoci = sociFisCoop + my_SociGiuCoop + my_SociFisFin + my_SociGiuFin + my_SociSpec;
        const sociFisCoopSecondo = toNum(getControl(my_EffettivitaBaseSocialeGroup, 'my_SociFisCoopSecondo')?.value);
        const sociGiuCoopSecondo = toNum(getControl(my_EffettivitaBaseSocialeGroup, 'my_SociGiuCoopSecondo')?.value);
        const sociFisFinSecondo = toNum(getControl(my_EffettivitaBaseSocialeGroup, 'my_SociFisFinSecondo')?.value);
        const sociGiuFinSecondo = toNum(getControl(my_EffettivitaBaseSocialeGroup, 'my_SociGiuFinSecondo')?.value);
        const sociSpecSecondo = toNum(getControl(my_EffettivitaBaseSocialeGroup, 'my_SociSpecSecondo')?.value);
        const totSociSecondo = sociFisCoopSecondo + sociGiuCoopSecondo + sociFisFinSecondo + sociGiuFinSecondo + sociSpecSecondo;

        const showErrorCondition = my_SociInseriti < totSoci && totSoci > 0 && chiusuraEsercizio === '31-12' && data1EsPrec === anno1__1;
        const showErrorSecondoCondition = my_SociInseriti < totSociSecondo && totSociSecondo > 0 && chiusuraEsercizio === '31-12' && data2EsPrec === anno1__1;

        const errors = my_SociInseritiControl.errors ? my_SociInseritiControl.errors : {};

        addRemoveError(isNilOrEmpty(my_SociInseritiControl?.value), errors, 'my_SociInseriti', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(showErrorCondition, errors, 'my_SociInseriti_primo', {
            status: INCORRECT_VALUE,
            message: 'Verificare il numero dei soci inseriti nella base sociale (primo esercizio precedente)',
        });

        addRemoveError(showErrorSecondoCondition, errors, 'my_SociInseriti_secondo', {
            status: INCORRECT_VALUE,
            message: 'Verificare il numero dei soci inseriti nella base sociale (secondo esercizio precedente)',
        });

        my_SociInseritiControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CapitaleSociale: (form: FormGroup) => {
        const datiBilancioGroup = getGroup(form, formPaths.datiBilancio);
        const my_CapitaleSocialeControl = getContribBiennaleControl(form, 'my_CapitaleSociale');
        const my_CapitaleSociale = toNum(my_CapitaleSocialeControl?.value);
        const my_Anno1__1: string = getContribBiennaleControl(form, 'my_Anno1__1')?.value;
        const my_BilAnno1: string = getControl(datiBilancioGroup, 'my_BilAnno1')?.value;
        const my_BilAnno2: string = getControl(datiBilancioGroup, 'my_BilAnno2')?.value;
        const my_StPaPsAI1 = toNum(getControl(datiBilancioGroup, 'my_StatoPatrimoniale__Passivo.my_StPaPsAI1')?.value);
        const my_StPaPsAI2 = toNum(getControl(datiBilancioGroup, 'my_StatoPatrimoniale__Passivo.my_StPaPsAI2')?.value);
        const anno1Condition = my_Anno1__1 === my_BilAnno1 && my_CapitaleSociale < my_StPaPsAI1 && !isNilOrEmpty(my_BilAnno1);
        const anno2Condition = my_Anno1__1 === my_BilAnno2 && my_CapitaleSociale < my_StPaPsAI2 && !isNilOrEmpty(my_BilAnno2);

        const errors = my_CapitaleSocialeControl.errors ? my_CapitaleSocialeControl.errors : {};

        addRemoveError(isNilOrEmpty(my_CapitaleSocialeControl?.value), errors, 'my_CapitaleSociale', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(anno1Condition || anno2Condition, errors, 'my_CapitaleSociale', {
            status: INCORRECT_VALUE,
            message: 'Verificare il capitale indicato nel bilancio caricato',
        });

        my_CapitaleSocialeControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_RicaviVendPrest: (form: FormGroup) => {
        const datiBilancioGroup = getGroup(form, formPaths.datiBilancio);
        const contoEconomicoGroup = getGroup(form, `${formPaths.datiBilancio}.my_ContoEconomico`);
        const my_RicaviVendPrestControl = getContribBiennaleControl(form, 'my_RicaviVendPrest');
        const my_RicaviVendPrest = toNum(my_RicaviVendPrestControl?.value);
        const my_Anno1__1: string = getContribBiennaleControl(form, 'my_Anno1__1')?.value;
        const my_BilAnno1: string = getControl(datiBilancioGroup, 'my_BilAnno1')?.value;
        const my_BilAnno2: string = getControl(datiBilancioGroup, 'my_BilAnno2')?.value;
        const my_CoEcAtot1 = toNum(getControl(contoEconomicoGroup, 'my_CoEcAtot1')?.value);
        const my_CoEcAtot2 = toNum(getControl(contoEconomicoGroup, 'my_CoEcAtot2')?.value);
        const anno1Condition = my_Anno1__1 === my_BilAnno1 && my_RicaviVendPrest < my_CoEcAtot1 && !isNilOrEmpty(my_BilAnno1);
        const anno2Condition = my_Anno1__1 === my_BilAnno2 && my_RicaviVendPrest < my_CoEcAtot2 && !isNilOrEmpty(my_BilAnno2);

        const errors = my_RicaviVendPrestControl.errors ? my_RicaviVendPrestControl.errors : {};

        addRemoveError(anno1Condition || anno2Condition, errors, 'my_RicaviVendPrest', {
            status: INCORRECT_VALUE,
            message: 'Verificare il valore della produzione indicato nel bilancio caricato',
        });

        addRemoveError(isNilOrEmpty(my_RicaviVendPrestControl?.value), errors, 'my_RicaviVendPrest', {
            status: REQUIRED_STATUS,
        });

        my_RicaviVendPrestControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
};
